/* =============================================================================
   app.js  —  STARTER BOOST · Cinematic 3D Can Animation
   =============================================================================

   ANIMATION PHILOSOPHY
   ─────────────────────
   The can should feel like a premium product suspended in air — it has
   weight, intention, and inertia. Nothing is random. Every move is a reveal.

   MOTION RULES (read before editing)
   ────────────────────────────────────
   1.  IDLE  →  slow, breathing sine waves. Never exceeds a few degrees.
               Motion lives on `can` (the child mesh). Totally organic.

   2.  SCROLL  →  cinematic arc paths (not straight lines). Lives on
                  `canGroup` (the parent). GSAP owns this. Render loop
                  never touches canGroup.

   3.  THE TWO-OBJECT RULE
       canGroup  ← GSAP scroll timelines animate this.  Render loop: never touch.
         └─ can  ← Render loop applies idle offsets to this. GSAP scroll: never touch.
       Because they're separate objects, their transforms stack with zero conflict.

   4.  CONTINUOUS TIMELINE
       tl1 covers the entire first scroll phase  (hero top → S1 top).
       It has three internal phases:
         Phase A — hero parallax  (first 12% of the scroll range)
         Phase B — arc through midpoint  (next 43%)
         Phase C — settle into S1 position  (final 45%)
       This one combined timeline eliminates the overlap/conflict
       that two separate triggers on the same scroll range would cause.

   5.  ARC MOTION
       Two keyframe positions per transition:  arc-mid  →  final.
       power2.in  arriving at the midpoint  (accelerates toward mid).
       power2.out settling into the final   (decelerates into final).
       This creates the diagonal-arc, ease-in/ease-out cinematic feel.

   SECTIONS
   ─────────
    1.  Imports & GSAP
    2.  Breakpoint helper
    3.  lerp helper
    4.  CONFIG  ← EDIT ALL POSITIONS & ROTATIONS HERE
    5.  Scene / camera / renderer
    6.  Lighting
    7.  Model loading
    8.  Scroll animations  (GSAP)
    9.  Render loop  (idle float + tilt)
   10.  Resize handler
   11.  Mouse & touch tracking
   12.  Lightning
   13.  Drink bubbles
   14.  Mouse parallax  (frost badge)
   15.  Glass card tilt
   16.  Energy bars
   17.  Flavor chips
   18.  Section reveal
   19.  Smooth anchor scroll

   ============================================================================= */


/* =============================================================================
   1.  IMPORTS & GSAP
   ============================================================================= */

import * as THREE          from 'three';
import { GLTFLoader }      from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

gsap.registerPlugin(ScrollTrigger);


/* =============================================================================
   2.  BREAKPOINT HELPER
   ============================================================================= */

function getBreakpoint() {
  const w = window.innerWidth;
  if (w < 641) return 'mobile';
  if (w < 901) return 'tablet';
  return 'desktop';
}

let bp = getBreakpoint();


/* =============================================================================
   3.  LERP HELPER
   =============================================================================
   Moves value `a` toward value `b` by `t` percent each call.
   Small t (0.03–0.06) = smooth, laggy follow.
   Large t (0.15–0.3)  = snappy, responsive follow.
   ============================================================================= */

function lerp(a, b, t) { return a + (b - a) * t; }


/* =============================================================================
   4.  CONFIG  — DESKTOP FIRST
   =============================================================================
   All positions, rotations, and camera settings live here.
   Tablet and mobile inherit the desktop spirit but with reduced travel.

   HOW THE ARC POSITIONS WORK
   ───────────────────────────
   Each transition has two target positions:
     s1ArcPos  — the "belly" of the arc (where the can passes through)
     s1Pos     — the final resting position

   The timeline plays to s1ArcPos first (with ease-in), then continues
   to s1Pos (with ease-out). This creates a curved, diagonal arc path
   instead of a boring straight line.

   COORDINATE SYSTEM
     x → negative = left,   positive = right
     y → negative = down,   positive = up
     z → negative = away,   positive = toward camera

   ROTATION  (radians)
     Math.PI      = 180°    Math.PI / 2 = 90°
     -0.35 rad    ≈ -20°    (hero slight Y turn)
     0.52 rad     ≈  30°    (S1 label reveal)

   TUNING TIPS
     • Move heroPos.x more negative to push the can further left.
     • Increase s1Pos.z for more dramatic zoom in S1.
     • Make s1ArcPos.y more negative for a deeper arc dip.
     • Increase s2Pos.y more negative for a lower resting S2 position.
   ============================================================================= */

const CONFIG = {

  // ─── DESKTOP  (≥ 901px)  —  the reference design ────────────────────────
  desktop: {
    fov:   50,
    camZ:  5.5,     // pulled back so full can height is always visible
    scale: 0.48,

    // ── Hero start state ──
    // Left side, slightly turned so label is partially visible
    heroPos: { x: -1.6, y: -0.60, z:  0.00 },
    heroRot: { x:  0.1, y: -0.6, z:  0.03 },

    // ── Hero parallax shift (phase A of tl1) ──
    // While the user scrolls through the hero section, the can subtly
    // shifts by this delta — giving the "anchored but reactive" feeling.
    heroParallaxDelta: { y: -0.12, ry: 0.06 },

    // ── S1 arc belly ──
    // The can passes THROUGH here on its way to s1Pos.
    // Dips lower (y) and comes forward (z) at the midpoint of the arc.
    s1ArcPos: { x:  0.00, y: -0.82, z:  0.55 },
    s1ArcRot: { x:  0.06, y:  0.25, z:  0.04 },

    // ── S1 final position ──
    // Center-right, closer to camera, label revealed
    s1Pos: { x:  1.6, y: -0.60, z:  0.80 },
    s1Rot: { x:  0.08, y:  1.5, z:  0.06 },

    // ── S2 arc belly ──s
    // Passes through here going from S1 back toward the left.
    // Pulls back on Z for depth contrast.
    s2ArcPos: { x:  0.10, y: -0.88, z:  0.30 },
    s2ArcRot: { x:  0.04, y:  1.10, z:  0.04 },

    // ── S2 final position ──
    // Left side again, lower, showing a different face of the can
    s2Pos: { x: -1.6, y: -1.02, z:  0.00 },
    s2Rot: { x: -0.04, y:  -2.5, z:  0.10 },
  },

  // ─── TABLET  (641–900px) ────────────────────────────────────────────────
  // Reduced horizontal travel, same rotation quality
  tablet: {
    fov:   54,
    camZ:  5.5,
    scale: 0.36,

    heroPos: { x: -0.85, y: -0.48, z:  0.00 },
    heroRot: { x:  0.10, y: -0.35, z:  0.02 },

    heroParallaxDelta: { y: -0.10, ry: 0.05 },

    s1ArcPos: { x:  0.00, y: -0.78, z:  0.35 },
    s1ArcRot: { x:  0.05, y:  0.16, z:  0.03 },

    s1Pos: { x:  0.90, y: -0.44, z:  0.50 },
    s1Rot: { x:  0.07, y:  0.52, z:  0.05 },

    s2ArcPos: { x:  0.05, y: -0.80, z:  0.20 },
    s2ArcRot: { x:  0.03, y:  1.00, z:  0.03 },

    s2Pos: { x: -0.90, y: -0.88, z:  0.00 },
    s2Rot: { x: -0.03, y:  1.92, z:  0.08 },
  },

  // ─── MOBILE  (< 641px) ──────────────────────────────────────────────────
  // Starts centred. Focus on depth (Z) and rotation, not wide horizontal sweeps.
  // The first move is a zoom-in effect (positive Z) before transitioning.
  mobile: {
    fov:   64,
    camZ:  6.0,
    scale: 0.28,

    // Centred — x = 0
    heroPos: { x:  0.00, y: -0.32, z:  0.00 },
    heroRot: { x:  0.10, y: -0.35, z:  0.00 },

    heroParallaxDelta: { y: -0.08, ry: 0.04 },

    // Arc: zoom toward camera (positive z) as the primary movement
    s1ArcPos: { x:  0.00, y: -0.32, z:  1.00 },   // zoom in first
    s1ArcRot: { x:  0.06, y:  0.15, z:  0.00 },

    // Settle: slightly pull back while completing rotation
    s1Pos: { x:  0.00, y: -0.32, z:  0.70 },
    s1Rot: { x:  0.08, y:  Math.PI * 0.55, z:  0.00 },

    // S2: pull back further, continue rotating, drop very slightly
    s2ArcPos: { x:  0.00, y: -0.44, z:  0.55 },
    s2ArcRot: { x:  0.03, y:  Math.PI * 0.80, z:  0.02 },

    s2Pos: { x:  0.00, y: -0.55, z:  0.25 },
    s2Rot: { x: -0.02, y:  Math.PI * 1.02, z:  0.03 },
  },
};


/* =============================================================================
   5.  SCENE / CAMERA / RENDERER
   ============================================================================= */

const scene = new THREE.Scene();
let   cfg   = CONFIG[bp];

// PerspectiveCamera(fov°, aspect-ratio, near-clip, far-clip)
const camera = new THREE.PerspectiveCamera(
  cfg.fov,
  window.innerWidth / window.innerHeight,
  0.1,
  100
);
camera.position.z = cfg.camZ;

// Transparent canvas — page background shows through
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.toneMapping         = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.6;
document.getElementById('canvas-container').appendChild(renderer.domElement);

// Reflective environment — makes metallic can surfaces catch light correctly
const pmrem = new THREE.PMREMGenerator(renderer);
scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;


/* =============================================================================
   6.  LIGHTING
   =============================================================================
   Multiple directional lights from different angles create the premium
   look — sharp highlights on one side, soft colour fills on the other.
   Tip: set intensity to 0 on any light to isolate its contribution.
   ============================================================================= */

// Ambient: equal fill across the entire scene — prevents pure-black shadows
scene.add(new THREE.AmbientLight(0xffffff, 0.40));

// Key light: main studio light, upper right — sharp white highlight
const keyLight = new THREE.DirectionalLight(0xffffff, 2.4);
keyLight.position.set(5, 5, 5);
scene.add(keyLight);

// Rim light: shines from behind the can — creates glowing edge outline
const rimLight = new THREE.DirectionalLight(0x00ffff, 1.0);
rimLight.position.set(-4, 2, -5);
scene.add(rimLight);

// Fill light: softens the shadow side — cool blue tint
const fillLight = new THREE.DirectionalLight(0x0066cc, 0.65);
fillLight.position.set(3, -2, 4);
scene.add(fillLight);

// Accent point: cyan glow near the can that ramps up as you enter Section 1
// Position drifts toward the mouse on desktop (see render loop)
const accentLight = new THREE.PointLight(0x00ffff, 0, 8);
accentLight.position.set(0, 2, 3);
scene.add(accentLight);


/* =============================================================================
   7.  MODEL LOADING
   =============================================================================
   canGroup  ← GSAP scroll timelines animate THIS
     └─ can  ← Idle render loop applies tiny float offsets to THIS

   These are two separate objects so their transforms never conflict.
   ============================================================================= */

let can       = null;
let canGroup  = null;
let activeSTs = [];   // live ScrollTrigger instances — killed before rebuild

const loadingScreen = document.getElementById('loading-screen');

new GLTFLoader().load(
  window.__SB_GLB_URL__ || 'starter-boost-can.glb',   // ← YOUR FILE — must be in the same folder as index.html

  // ── SUCCESS ──────────────────────────────────────────────────────────────
  function onLoad(gltf) {

    can = gltf.scene;

    // canGroup is the parent — GSAP will move this
    canGroup = new THREE.Group();
    scene.add(canGroup);

    // Reset can's own transform — the render loop adds offsets each frame
    can.position.set(0, 0, 0);
    can.rotation.set(0, 0, 0);
    canGroup.add(can);

    // Set starting scale + position
    applyConfig(cfg);

    // Improve reflections on all mesh surfaces in the model
    can.traverse(node => {
      if (node.isMesh && node.material) {
        node.material.envMapIntensity = 0.8;
        node.material.needsUpdate     = true;
      }
    });

    // Entrance animation — targets `can` ONLY, not canGroup
    // This means GSAP scroll triggers on canGroup have zero conflict
    gsap.from(can.position, { y: -2.2, duration: 1.6, ease: 'power3.out', delay: 0.1 });
    gsap.from(can.rotation, { y: -Math.PI, duration: 2.0, ease: 'power3.out', delay: 0.1 });

    // Fade out loading screen
    if (loadingScreen) {
      loadingScreen.classList.add('hidden');
      setTimeout(() => loadingScreen?.remove(), 900);
    }

    buildScrollAnimations();
    initSectionReveal();
  },
// ── PROGRESS ─────────────────────────────────────────────────────────────
  function onProgress(xhr) {
    if (xhr.lengthComputable) {
      const pct = Math.min(100, Math.round((xhr.loaded / xhr.total) * 100));
      const bar  = document.querySelector('.loader-bar');
      const text = document.querySelector('.loader-text');
      if (bar)  bar.style.setProperty('--pct', pct + '%');
      if (text) text.textContent = 'Loading STARTER BOOST — ' + pct + '%';
    }
  },

  function onError(err) {
    console.error('GLB load error:', err);
    const t = loadingScreen?.querySelector('.loader-text');
    if (t) t.textContent = 'Error — check filename & console (F12)';
  }
);


// Sets canGroup position/rotation and can scale from a config object
function applyConfig(c) {
  if (!canGroup || !can) return;
  can.scale.set(c.scale, c.scale, c.scale);
  canGroup.position.set(c.heroPos.x, c.heroPos.y, c.heroPos.z);
  canGroup.rotation.set(c.heroRot.x, c.heroRot.y, c.heroRot.z);
}


/* =============================================================================
   8.  SCROLL ANIMATIONS  (GSAP + ScrollTrigger)
   =============================================================================

   TIMELINE 1  (tl1) — covers the entire first scroll phase
   ──────────────────────────────────────────────────────────
   Trigger:  hero section top → Section 1 top  (one viewport height of scroll)
   Why one timeline: using two separate triggers for hero parallax and S1
   transition causes them to overlap and fight over the same properties.
   One timeline = one owner = zero conflict.

   Three internal phases (durations sum to 1.0):
     Phase A  [0.00 → 0.12]  Hero parallax: tiny reactive shift as you begin scrolling
     Phase B  [0.12 → 0.55]  Arc belly:     diagonal move through the midpoint
     Phase C  [0.55 → 1.00]  Settle:        ease into S1 final position

   The arc belly + settle combination creates the cinematic diagonal arc.
   power2.in drives INTO the arc, power2.out SETTLES out of it.

   TIMELINE 2  (tl2) — covers the S1 → S2 transition
   ────────────────────────────────────────────────────
   Trigger:  S2 top enters viewport bottom → S2 top reaches viewport top
   Same arc-belly + settle structure, but in the opposite direction.

   SCRUB
   ──────
   scrub: 2.8  →  the animation position lags 2.8 s behind scroll.
   This gives the inertia / slight delay that makes motion feel weighted.
   Increase for dreamier motion, decrease for snappier response.
   ============================================================================= */

function buildScrollAnimations() {
 
  // Kill all previous instances before rebuilding (required on resize)
  activeSTs.forEach(st => st?.kill());
  activeSTs = [];
  ScrollTrigger.getAll().forEach(st => st.kill());
 
  if (!canGroup) return;
  const c = CONFIG[bp];
 
  // ── Hero → Section 1  (same structure as TL2) ───────────────────────────
  const tl1 = gsap.timeline({
    scrollTrigger: {
      trigger:   '#section-1',
      start:     'top bottom',
      end:       'top top',
      scrub:     2.5,
      overwrite: 'auto',
    },
  });
 
  tl1.to(canGroup.position, {
    x:        c.s1Pos.x,
    y:        c.s1Pos.y,
    z:        c.s1Pos.z,
    ease:     'power2.inOut',
    duration: 1.0,
  }, 0);
 
  tl1.to(canGroup.rotation, {
    x:        c.s1Rot.x,
    y:        c.s1Rot.y,
    z:        c.s1Rot.z,
    ease:     'power2.inOut',
    duration: 1.0,
  }, 0);
 
  tl1.to(accentLight, { intensity: 1.2, ease: 'power2.inOut', duration: 1.0 }, 0);
 
  activeSTs.push(tl1.scrollTrigger);
 
 
  // ── Section 1 → Section 2  (diagonal arc: CENTER-RIGHT → LEFT + pullback) ─
  const tl2 = gsap.timeline({
    scrollTrigger: {
      trigger:   '#section-2',
      start:     'top bottom',
      end:       'top top',
      scrub:     2.5,
      overwrite: 'auto',
    },
  });
 
  tl2.to(canGroup.position, {
    x:        c.s2Pos.x,
    y:        c.s2Pos.y,
    z:        c.s2Pos.z,
    ease:     'power2.inOut',
    duration: 1.0,
  }, 0);
 
  tl2.to(canGroup.rotation, {
    x:        c.s2Rot.x,
    y:        c.s2Rot.y,
    z:        c.s2Rot.z,
    ease:     'power2.inOut',
    duration: 1.0,
  }, 0);
 
  // Accent light dims slightly in Section 2 (settled state = calmer)
  tl2.to(accentLight, { intensity: 0.6, ease: 'power2.inOut', duration: 1.0 }, 0);
 
  activeSTs.push(tl2.scrollTrigger);
 
  ScrollTrigger.refresh();
}

// function buildScrollAnimations() {

//   // Kill all previous triggers — required when rebuilding on resize
//   activeSTs.forEach(st => st?.kill());
//   activeSTs = [];

//   if (!canGroup) return;
//   const c = CONFIG[bp];

//   // Compute hero parallax targets (heroPos + delta)
//   const heroParaY  = c.heroPos.y  + c.heroParallaxDelta.y;
//   const heroParaRY = c.heroRot.y  + c.heroParallaxDelta.ry;

//   // ── TIMELINE 1  (hero parallax + S1 arc) ─────────────────────────────────
//   const tl1 = gsap.timeline({
//     scrollTrigger: {
//       trigger:    '#section-hero',   // starts watching from hero section
//       start:      'top top',         // begins when hero top hits viewport top
//       endTrigger: '#section-1',      // ends watching at S1
//       end:        'top top',         // ends when S1 top hits viewport top
//       scrub:      3,               // smooth lag — the premium "weighted" feel
//     },
//   });

//   // Phase A: Hero parallax (duration 0.12 out of 1.0)
//   // Tiny Y drop and slight Y rotation begin — the can "reacts" to scrolling
//   tl1.to(canGroup.position,
//     { y: heroParaY, ease: 'none', duration: 0.12 }, 0);
//   tl1.to(canGroup.rotation,
//     { y: heroParaRY, ease: 'none', duration: 0.12 }, 0);

//   // Phase B: Arc belly (duration 0.43 — starts where Phase A ends)
//   // power2.in: accelerates toward the midpoint (feels like it's being pulled)
//   tl1.to(canGroup.position,
//     { x: c.s1ArcPos.x, y: c.s1ArcPos.y, z: c.s1ArcPos.z,
//       ease: 'power2.in', duration: 0.43 });
//   tl1.to(canGroup.rotation,
//     { x: c.s1ArcRot.x, y: c.s1ArcRot.y, z: c.s1ArcRot.z,
//       ease: 'power2.in', duration: 0.43 }, '<');   // '<' = same start as previous

//   // Phase C: Settle (duration 0.45 — starts where Phase B ends)
//   // power2.out: decelerates smoothly into the final position (feels weighted)
//   tl1.to(canGroup.position,
//     { x: c.s1Pos.x, y: c.s1Pos.y, z: c.s1Pos.z,
//       ease: 'power2.out', duration: 0.45 });
//   tl1.to(canGroup.rotation,
//     { x: c.s1Rot.x, y: c.s1Rot.y, z: c.s1Rot.z,
//       ease: 'power2.out', duration: 0.45 }, '<');

//   // Accent light ramps up as the can arrives at S1 (starts at arc phase)
//   tl1.to(accentLight, { intensity: 1.0, ease: 'power2.inOut', duration: 0.60 }, 0.30);

//   activeSTs.push(tl1.scrollTrigger);


//   // ── TIMELINE 2  (S1 → S2 arc) ────────────────────────────────────────────
//   const tl2 = gsap.timeline({
//     scrollTrigger: {
//       trigger: '#section-2',
//       start:   'top bottom',   // S2 top enters viewport from below
//       end:     'top top',      // S2 top reaches viewport top
//       scrub:   3,
//     },
//   });

//   // Arc belly — moves through midpoint on its way left (or down on mobile)
//   tl2.to(canGroup.position,
//     { x: c.s2ArcPos.x, y: c.s2ArcPos.y, z: c.s2ArcPos.z,
//       ease: 'power2.in', duration: 0.42 }, 0);
//   tl2.to(canGroup.rotation,
//     { x: c.s2ArcRot.x, y: c.s2ArcRot.y, z: c.s2ArcRot.z,
//       ease: 'power2.in', duration: 0.42 }, 0);

//   // Settle into S2 final position
//   tl2.to(canGroup.position,
//     { x: c.s2Pos.x, y: c.s2Pos.y, z: c.s2Pos.z,
//       ease: 'power2.out', duration: 0.58 });
//   tl2.to(canGroup.rotation,
//     { x: c.s2Rot.x, y: c.s2Rot.y, z: c.s2Rot.z,
//       ease: 'power2.out', duration: 0.58 }, '<');

//   // Accent light dims as the can moves into S2
//   tl2.to(accentLight, { intensity: 0.45, ease: 'power2.inOut', duration: 0.60 }, 0.20);

//   activeSTs.push(tl2.scrollTrigger);

//   ScrollTrigger.refresh();
// }


/* =============================================================================
   9.  RENDER LOOP  — organic idle float + tilt
   =============================================================================
   Runs every frame via requestAnimationFrame (≈ 60 fps).

   IMPORTANT: Only ever touches `can` (the child mesh).
   `canGroup` is exclusively owned by GSAP. Never += or = on canGroup here.

   THE IDLE FLOAT
   ───────────────
   Multiple sine waves at different frequencies create motion that never
   settles into a recognisable loop — it feels organic and alive.

   The phase offsets (+1.2, +0.5, +2.1, +3.4) ensure that on page load
   the can doesn't start in a 0-rotation, 0-position state — the motion
   is already mid-cycle and feels continuous.

   ALL VALUES ARE SET (=) NOT ACCUMULATED (+=)
   ─────────────────────────────────────────────
   Setting absolutes from fresh calculations each frame means the values
   can never drift. Accumulation was the source of the old glitches.

   TILT PER DEVICE
   ────────────────
   Desktop: the can subtly "looks" toward the mouse cursor.
   Mobile/tablet: a slow automatic sway using different-frequency sine waves
                  on X and Y — never repeats, never looks mechanical.

   lerp smoothing (0.04) gives the tilt a "weight" — it doesn't snap
   instantly to target, it glides with noticeable but premium delay.
   ============================================================================= */

const clock = new THREE.Clock();
const mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

// Tilt state — target is where we WANT to be, current is where we ARE
const tilt = { targetX: 0, targetY: 0, currentX: 0, currentY: 0 };

function animate() {
  requestAnimationFrame(animate);

  if (can && canGroup) {
    const t = clock.getElapsedTime();   // seconds since page load

    // ── A.  Idle float ───────────────────────────────────────────────────
    // Phase offsets (+1.2, etc.) make it feel like the motion was already
    // in progress when the page loaded — fully organic from frame 1.

    // Slow vertical breathing bob — barely noticeable, like the can is alive
    const bobY   = Math.sin(t * 0.38 + 1.2) * 0.030;

    // Micro Y rotation drift — can very slowly yaws left and right
    const microY = Math.sin(t * 0.17 + 0.5) * 0.016;

    // Micro Z rotation drift — can very slightly rolls (different rhythm)
    const microZ = Math.cos(t * 0.23 + 3.4) * 0.009;

    // Micro X pitch — almost imperceptible forward/back lean
    const microX = Math.sin(t * 0.28 + 2.1) * 0.007;


    // ── B.  Tilt (device-specific) ───────────────────────────────────────

    if (bp === 'desktop') {
      // Desktop: can subtly follows the mouse
      // nx/ny normalised to -1 … +1  (0 = screen centre)
      const nx = (mouse.x / window.innerWidth  - 0.5) * 2;
      const ny = (mouse.y / window.innerHeight - 0.5) * 2;
      tilt.targetY =  nx * 0.10;   // max ≈ ±6° horizontal look
      tilt.targetX = -ny * 0.06;   // max ≈ ±3.5° vertical look

    } else {
      // Mobile / tablet: gentle automatic sway — no mouse required.
      // Two completely different frequencies so it never repeats obviously.
      tilt.targetX = Math.sin(t * 0.30 + 1.8) * 0.065;   // front/back ≈ ±3.7°
      tilt.targetY = Math.sin(t * 0.21 + 0.9) * 0.055;   // left/right ≈ ±3.2°
    }

    // Lerp smoothing — current chases target at 4% per frame
    // This is where the "weight" and "inertia" feeling comes from
    tilt.currentX = lerp(tilt.currentX, tilt.targetX, 0.04);
    tilt.currentY = lerp(tilt.currentY, tilt.targetY, 0.04);


    // ── C.  Apply ONLY to `can` (the child) ─────────────────────────────
    // canGroup's transforms are exclusively managed by GSAP above.
    // Touching canGroup here would cause the glitching we fixed.

    can.position.y = bobY;
    can.rotation.x = microX + tilt.currentX;
    can.rotation.y = microY + tilt.currentY;
    can.rotation.z = microZ;


    // ── D.  Accent light follows mouse on desktop ────────────────────────
    // Lerp at 3% — slower than tilt, so the light lags behind the cursor
    // giving it a trailing, atmospheric feel.
    if (bp === 'desktop') {
      accentLight.position.x = lerp(
        accentLight.position.x,
        (mouse.x / window.innerWidth  - 0.5) * 5,
        0.03
      );
      accentLight.position.y = lerp(
        accentLight.position.y,
        -(mouse.y / window.innerHeight - 0.5) * 4,
        0.03
      );
    }
  }

  renderer.render(scene, camera);
}

animate();


/* =============================================================================
   10.  RESIZE HANDLER  (debounced 150 ms)
   =============================================================================
   On resize: update breakpoint → recalculate camera → resize renderer
   → snap can back to hero position → rebuild scroll animations.
   Debounced so it doesn't fire 60× per second while dragging the window.
   ============================================================================= */

let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {

    bp  = getBreakpoint();
    cfg = CONFIG[bp];

    camera.fov        = cfg.fov;
    camera.aspect     = window.innerWidth / window.innerHeight;
    camera.position.z = cfg.camZ;
    camera.updateProjectionMatrix();   // must call after any camera change

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    applyConfig(cfg);
    buildScrollAnimations();

  }, 150);
});


/* =============================================================================
   11.  MOUSE & TOUCH TRACKING
   ============================================================================= */

document.addEventListener('mousemove', e => {
  mouse.x = e.clientX;
  mouse.y = e.clientY;
});

document.addEventListener('touchmove', e => {
  if (e.touches.length) {
    mouse.x = e.touches[0].clientX;
    mouse.y = e.touches[0].clientY;
  }
}, { passive: true });


/* =============================================================================
   12.  LIGHTNING
   =============================================================================
   Occasional electric bolts that strike toward the mouse cursor.
   Drawn on #lightning-canvas with a recursive fractal algorithm.

   FREQUENCY:  every 8 – 15 seconds (rare — feels like a real event).
               30% chance of a quick double-strike.

   HOW drawBolt WORKS
   ───────────────────
   1.  Takes two endpoints: (x1,y1) → (x2,y2)
   2.  Finds the midpoint and randomly offsets it for the jag
   3.  Draws the two line segments (start→mid, mid→end)
   4.  Recursively calls itself on each half with fading alpha
   5.  Occasionally spawns a side branch at each level

   TUNING
   ───────
   • Change "8000 + Math.random() * 7000" for frequency
   • Change depth "6" for more/fewer branches (higher = more complex, heavier)
   • Change endX/endY spread "±90 / ±70" for tighter/looser mouse targeting
   ============================================================================= */

(function initLightning() {
  const canvas = document.getElementById('lightning-canvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');

  function resize() { canvas.width = window.innerWidth; canvas.height = window.innerHeight; }
  resize();
  window.addEventListener('resize', resize);

  function drawBolt(x1, y1, x2, y2, depth, alpha) {
    if (depth === 0 || alpha < 0.03) return;

    // Randomly offset the midpoint to create the jagged look
    const mx = (x1 + x2) / 2 + (Math.random() - 0.5) * Math.abs(x2 - x1) * 0.60;
    const my = (y1 + y2) / 2 + (Math.random() - 0.5) * Math.abs(y2 - y1) * 0.60;

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(mx, my);
    ctx.lineTo(x2, y2);
    ctx.lineWidth   = depth * 0.55;
    ctx.strokeStyle = `rgba(0,235,255,${alpha.toFixed(2)})`;
    ctx.shadowBlur  = 18;
    ctx.shadowColor = 'rgba(0,255,255,0.90)';
    ctx.stroke();

    // 50% chance of a random side branch
    if (depth > 1 && Math.random() > 0.50) {
      drawBolt(
        mx, my,
        mx + (Math.random() - 0.5) * 160,
        my + Math.random() * 100,
        depth - 1, alpha * 0.42
      );
    }

    drawBolt(x1, y1, mx, my, depth - 1, alpha * 0.72);
    drawBolt(mx, my, x2, y2, depth - 1, alpha * 0.72);
  }

  function strike() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.globalAlpha = 1;

    // Start from a random point near the top of the screen
    const startX = mouse.x + (Math.random() - 0.5) * canvas.width * 0.85;
    const startY = -20;

    // Strike toward the mouse with a small spread
    const endX = mouse.x + (Math.random() - 0.5) * 90;
    const endY = mouse.y + (Math.random() - 0.5) * 70;

    drawBolt(startX, startY, endX, endY, 6, 0.90);

    // Impact glow at the mouse position
    const g = ctx.createRadialGradient(endX, endY, 0, endX, endY, 50);
    g.addColorStop(0, 'rgba(0,255,255,0.42)');
    g.addColorStop(1, 'rgba(0,255,255,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(endX, endY, 50, 0, Math.PI * 2);
    ctx.fill();

    // Fade out over ~500 ms
    let a = 1;
    const fade = setInterval(() => {
      a -= 0.09;
      if (a <= 0) { clearInterval(fade); ctx.clearRect(0, 0, canvas.width, canvas.height); }
      else ctx.globalAlpha = a;
    }, 45);
  }

  // Random schedule — 8 to 15 seconds between strikes
  function schedule() {
    setTimeout(() => {
      strike();
      if (Math.random() > 0.70) setTimeout(strike, 160);   // 30% double-strike
      schedule();
    }, 8000 + Math.random() * 7000);
  }

  setTimeout(strike, 2000);   // first strike 2 s after page load
  schedule();

})();


/* =============================================================================
   13.  DRINK BUBBLES
   =============================================================================
   Subtle carbonation-style bubbles rising from the bottom of the screen.
   Drawn on #particle-canvas as glass circles with a highlight reflection.

   BEHAVIOUR
     Spawn: 2–4 bubbles per burst, every 4–8 seconds
     Each bubble: rises upward with slow sine-wave wobble
     Rendered: circle outline (glass look) + tiny highlight dot
     Fade: opacity decreases as `life` depletes from 1.0 → 0

   TUNING
     count     → "2 + Math.floor(Math.random() * 3)"  for more/fewer per burst
     delay     → "4000 + Math.random() * 4000"         for frequency
     vy        → "-(0.4 + Math.random() * 0.6)"        for rise speed
     alpha     → "0.12 + Math.random() * 0.18"         for visibility
   ============================================================================= */

(function initDrinkBubbles() {
  const canvas = document.getElementById('particle-canvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');

  function resize() { canvas.width = window.innerWidth; canvas.height = window.innerHeight; }
  resize();
  window.addEventListener('resize', resize);

  let bubbles = [];

  function spawnBubbles() {
    const count = 2 + Math.floor(Math.random() * 3);   // 2 – 4 per burst
    for (let i = 0; i < count; i++) {
      bubbles.push({
        x:           Math.random() * window.innerWidth,
        y:           window.innerHeight + 10,
        r:           3 + Math.random() * 11,             // 3–14 px radius
        vy:         -(0.4 + Math.random() * 0.6),        // upward drift
        alpha:       0.12 + Math.random() * 0.18,        // 0.12–0.30 opacity
        life:        1.0,
        decay:       0.0010 + Math.random() * 0.0014,    // lifespan
        wobble:      Math.random() * Math.PI * 2,        // starting phase
        wobbleSpeed: 0.010 + Math.random() * 0.012,      // wobble frequency
      });
    }
  }

  function drawBubble(b) {
    const a = b.alpha * b.life;

    // Outer circle — transparent stroke gives the glass look
    ctx.beginPath();
    ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(160,230,255,${a.toFixed(3)})`;
    ctx.lineWidth   = 0.8;
    ctx.stroke();

    // Inner highlight — tiny bright dot at upper-left of the circle
    // This detail is what makes it read as a real glass bubble
    const hlR = Math.max(0.5, b.r * 0.22);
    ctx.beginPath();
    ctx.arc(b.x - b.r * 0.30, b.y - b.r * 0.30, hlR, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(220,245,255,${(a * 0.55).toFixed(3)})`;
    ctx.fill();
  }

  function drawBubbles() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = bubbles.length - 1; i >= 0; i--) {
      const b = bubbles[i];

      b.wobble += b.wobbleSpeed;
      b.x      += Math.sin(b.wobble) * 0.25;   // gentle horizontal sway
      b.y      += b.vy;
      b.life   -= b.decay;

      if (b.life <= 0 || b.y < -b.r * 2) { bubbles.splice(i, 1); continue; }

      drawBubble(b);
    }

    requestAnimationFrame(drawBubbles);
  }

  requestAnimationFrame(drawBubbles);

  function scheduleBubbles() {
    setTimeout(() => {
      spawnBubbles();
      scheduleBubbles();
    }, 4000 + Math.random() * 4000);
  }

  spawnBubbles();         // first burst on load
  scheduleBubbles();      // then on schedule

})();


/* =============================================================================
   14.  MOUSE PARALLAX  (frost badge — desktop only)
   =============================================================================
   The frost badge drifts very slightly as the mouse moves.
   lerp smoothing at 4% per frame creates a silky follow — not a snap.

   TUNING
     s value (0.008) — lower = more subtle. 0.015 = noticeably active.
   ============================================================================= */

(function initMouseParallax() {
  const targets = [
    { sel: '.frost-badge', s: 0.008, cx: 0, cy: 0 },
  ]
  .map(t => ({ ...t, el: document.querySelector(t.sel) }))
  .filter(t => t.el !== null);

  if (!targets.length) return;

  (function tick() {
    if (getBreakpoint() === 'desktop') {
      const ox = mouse.x - window.innerWidth  / 2;
      const oy = mouse.y - window.innerHeight / 2;
      targets.forEach(target => {
        target.cx = lerp(target.cx, ox * target.s, 0.04);
        target.cy = lerp(target.cy, oy * target.s, 0.04);
        target.el.style.transform = `translate(${target.cx.toFixed(2)}px, ${target.cy.toFixed(2)}px)`;
      });
    }
    requestAnimationFrame(tick);
  })();
})();


/* =============================================================================
   15.  GLASS CARD TILT  (desktop hover)
   =============================================================================
   .glass-card elements tilt toward the cursor while hovered.
   CSS perspective gives the depth. Springs back flat on mouseleave.
   ============================================================================= */

(function initCardTilt() {
  document.querySelectorAll('.glass-card').forEach(card => {
    card.addEventListener('mousemove', e => {
      if (getBreakpoint() !== 'desktop') return;
      const rect = card.getBoundingClientRect();
      const dx   = (e.clientX - (rect.left + rect.width  / 2)) / (rect.width  / 2);
      const dy   = (e.clientY - (rect.top  + rect.height / 2)) / (rect.height / 2);
      card.style.transform =
        `perspective(5000px) rotateX(${(-dy * 3.5).toFixed(2)}deg) rotateY(${(dx * 4.5).toFixed(2)}deg) translateZ(4px)`;
    });
    card.addEventListener('mouseleave', () => { card.style.transform = ''; });
  });
})();


/* =============================================================================
   16.  ENERGY BARS
   =============================================================================
   When Section 1 enters view, animates .ebar-fill width from 0 to
   whatever is in their data-width attribute (e.g. data-width="85").
   CSS cubic-bezier handles the animated transition (1.4 s).
   ============================================================================= */

(function initEnergyBars() {
  const fills    = document.querySelectorAll('.ebar-fill');
  const section1 = document.getElementById('section-1');
  if (!fills.length || !section1) return;

  let done = false;
  new IntersectionObserver(entries => {
    if (entries[0].isIntersecting && !done) {
      done = true;
      fills.forEach(f => { f.style.width = (f.dataset.width || '80') + '%'; });
    }
  }, { threshold: 0.25 }).observe(section1);
})();


/* =============================================================================
   17.  FLAVOR CHIPS
   =============================================================================
   Click to select. CSS class "active-flavor" handles visual state.
   Only one chip active at a time.
   ============================================================================= */

document.querySelectorAll('.flavor-chip').forEach(chip => {
  chip.addEventListener('click', () => {
    document.querySelectorAll('.flavor-chip').forEach(c => c.classList.remove('active-flavor'));
    chip.classList.add('active-flavor');
  });
});


/* =============================================================================
   18.  SECTION REVEAL
   =============================================================================
   IntersectionObserver adds "in-view" to sections when ≥ 18% is visible.
   CSS transitions on .headline / .eyebrow / etc. handle the visual fade-in.
   ============================================================================= */

function initSectionReveal() {
  const sections = document.querySelectorAll('.section');
  if (!sections.length) return;

  const obs = new IntersectionObserver(
    entries => entries.forEach(e => e.target.classList.toggle('in-view', e.isIntersecting)),
    { threshold: 0.18 }
  );

  sections.forEach(s => obs.observe(s));
}


/* =============================================================================
   19.  SMOOTH ANCHOR SCROLL
   =============================================================================
   Overrides default <a href="#id"> jump with a smooth scroll.
   ============================================================================= */

document.querySelectorAll('a[href^="#"]').forEach(link => {
  link.addEventListener('click', e => {
    e.preventDefault();
    const target = document.querySelector(link.getAttribute('href'));
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  });
});